import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaArrowsAlt, FaCompressAlt, FaExternalLinkAlt, FaMinus, FaTh, FaTimes } from 'react-icons/fa';
import { getMeetingDisplayName } from '../../utils/meetingDisplayName';

const MONITOR_LAYOUT_KEY = 'meeting.screenShareParticipantMonitor.v1';
const MONITOR_MODES = new Set(['left', 'right']);
const DOCK_MODES = ['left', 'right'];
const PIP_CONTROL_STYLE = {
  display: 'inline-grid',
  placeItems: 'center',
  minWidth: 26,
  height: 26,
  padding: '0 7px',
  border: '1px solid rgba(255,255,255,.2)',
  borderRadius: 7,
  background: 'rgba(255,255,255,.08)',
  color: 'white',
  cursor: 'pointer',
  fontSize: 10,
  fontWeight: 700,
};

const toId = (value) => String(value?._id || value?.id || value || '');

export const normalizeParticipantMonitorMode = (value) => (
  MONITOR_MODES.has(String(value || '').toLowerCase())
    ? String(value).toLowerCase()
    : 'right'
);

const readLayout = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(MONITOR_LAYOUT_KEY) || '{}');
    return {
      // Older releases stored top/bottom/float. Only left and right are valid
      // now, so stale layouts can never silently restore a removed mode.
      mode: normalizeParticipantMonitorMode(parsed.mode),
      x: Number.isFinite(parsed.x) ? parsed.x : 20,
      y: Number.isFinite(parsed.y) ? parsed.y : 84,
      width: Number.isFinite(parsed.width) ? parsed.width : 336,
      height: Number.isFinite(parsed.height) ? parsed.height : 220,
    };
  } catch {
    return { mode: 'right', x: 20, y: 84, width: 336, height: 220 };
  }
};

const asVideoStream = (source) => {
  if (!source) return null;
  if (typeof source.getVideoTracks === 'function') return source;
  const nested = source.stream;
  if (nested && typeof nested.getVideoTracks === 'function') return nested;
  const track = source.video?.kind === 'video'
    ? source.video
    : source.kind === 'video'
      ? source
      : null;
  if (!track || typeof MediaStream !== 'function') return null;
  return new MediaStream([track]);
};

const MonitorVideoTile = ({ tile, surfaceId, onVideoNode }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    video.srcObject = tile.stream || null;
    if (tile.stream) video.play?.().catch(() => undefined);
    onVideoNode?.(`${surfaceId}:${tile.key}`, video, tile);
    return () => {
      onVideoNode?.(`${surfaceId}:${tile.key}`, null, tile);
      video.srcObject = null;
    };
  }, [onVideoNode, surfaceId, tile]);

  const hasLiveVideo = Boolean(tile.stream?.getVideoTracks?.().some(
    (track) => track.readyState === 'live' && track.enabled !== false
  ));
  return (
    <article style={{ position: 'relative', minWidth: 0, minHeight: 0, overflow: 'hidden', borderRadius: 10, background: '#0f172a', border: '1px solid rgba(255,255,255,.18)' }}>
      {hasLiveVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: tile.local ? 'scaleX(-1)' : undefined }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 24, fontWeight: 800, color: 'white', background: 'linear-gradient(135deg,#312e81,#0f172a)' }}>
          {String(tile.name || '?').charAt(0).toUpperCase()}
        </div>
      )}
      <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '3px 7px', background: 'rgba(0,0,0,.68)', color: 'white', fontSize: 10 }}>
        {tile.name}{tile.local ? ' (You)' : ''}
      </span>
    </article>
  );
};

const MonitorSurface = ({ tiles, surfaceId, onVideoNode, vertical = false }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: vertical ? '1fr' : `repeat(${Math.min(Math.max(tiles.length, 1), 3)}, minmax(0, 1fr))`,
      gridAutoRows: vertical ? 'minmax(100px, 1fr)' : 'minmax(0, 1fr)',
      gap: 6,
      width: '100%',
      height: '100%',
      minHeight: 0,
      overflow: 'auto',
      padding: 6,
      background: '#020617',
    }}
  >
    {tiles.length ? tiles.map((tile) => (
      <MonitorVideoTile key={tile.key} tile={tile} surfaceId={surfaceId} onVideoNode={onVideoNode} />
    )) : (
      <div style={{ display: 'grid', placeItems: 'center', color: '#cbd5e1', fontSize: 12 }}>
        Waiting for participant video…
      </div>
    )}
  </div>
);

const copyStylesToPictureInPicture = (targetDocument) => {
  document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    try { targetDocument.head.appendChild(node.cloneNode(true)); } catch {}
  });
  const base = targetDocument.createElement('style');
  base.textContent = 'html,body,#participant-monitor-pip-root{width:100%;height:100%;margin:0;overflow:hidden;background:#020617;font-family:system-ui,sans-serif}*{box-sizing:border-box}';
  targetDocument.head.appendChild(base);
};

const finiteNumber = (value, fallback) => (
  Number.isFinite(Number(value)) ? Number(value) : fallback
);

/**
 * Window placement is deliberately best-effort. Document Picture-in-Picture
 * windows are owned by the browser/OS and some browsers ignore moveTo or
 * resizeTo. Keeping the calculation here lets supporting desktop shells dock
 * the monitor while every other browser safely falls back to the saved
 * in-meeting edge position.
 */
export const getPictureInPictureDockGeometry = (position, targetWindow = window) => {
  const screenInfo = targetWindow?.screen || window.screen || {};
  const availableLeft = finiteNumber(screenInfo.availLeft, 0);
  const availableTop = finiteNumber(screenInfo.availTop, 0);
  const availableWidth = Math.max(320, finiteNumber(screenInfo.availWidth, window.innerWidth || 1280));
  const availableHeight = Math.max(240, finiteNumber(screenInfo.availHeight, window.innerHeight || 720));
  const margin = 16;
  const normalizedPosition = normalizeParticipantMonitorMode(position);
  const width = Math.min(360, Math.max(280, availableWidth - margin * 2));
  const height = Math.min(520, Math.max(220, availableHeight - margin * 2));
  const centeredY = availableTop + Math.round((availableHeight - height) / 2);

  return {
    width,
    height,
    x: normalizedPosition === 'left'
      ? availableLeft + margin
      : availableLeft + availableWidth - width - margin,
    y: centeredY,
  };
};

const ScreenShareParticipantMonitor = ({
  active,
  localStream,
  remoteStreams = {},
  currentRoom,
  currentSessionId,
}) => {
  const initialLayout = useRef(readLayout()).current;
  const [mode, setMode] = useState(initialLayout.mode);
  const [frame] = useState(initialLayout);
  const [panelState, setPanelState] = useState('open');
  const [pipWindow, setPipWindow] = useState(null);
  const [standardPipActive, setStandardPipActive] = useState(false);
  const [restoreExternal, setRestoreExternal] = useState(false);
  const [externalStatus, setExternalStatus] = useState('');
  const rootRef = useRef(null);
  const pipWindowRef = useRef(null);
  const standardPipVideoRef = useRef(null);
  const videoNodesRef = useRef(new Map());
  const dragRef = useRef(null);

  const tiles = useMemo(() => {
    const participants = (currentRoom?.participants || []).filter((participant) =>
      ['admitted', 'presenting'].includes(String(participant.status)) &&
      participant.disconnected !== true
    );
    const values = participants.map((participant) => {
      const participantSessionId = String(participant.sessionId || '');
      const local = participantSessionId === String(currentSessionId || '');
      return {
        key: participantSessionId || toId(participant.user),
        local,
        name: getMeetingDisplayName(participant, 'Participant'),
        stream: local ? asVideoStream(localStream) : asVideoStream(remoteStreams[participantSessionId]),
      };
    }).filter((tile) => tile.key);
    if (!values.some((tile) => tile.local) && localStream) {
      values.push({ key: String(currentSessionId || 'local'), local: true, name: 'You', stream: asVideoStream(localStream) });
    }
    // Remote participants first makes standard one-video PiP useful to the presenter.
    return values.sort((left, right) => Number(left.local) - Number(right.local));
  }, [currentRoom?.participants, currentSessionId, localStream, remoteStreams]);

  const persistLayout = useCallback((nextMode = mode, nextFrame = frame) => {
    try { localStorage.setItem(MONITOR_LAYOUT_KEY, JSON.stringify({ ...nextFrame, mode: nextMode })); } catch {}
  }, [frame, mode]);

  useEffect(() => {
    // Rewrite stale top/bottom/float storage immediately after mounting so
    // future opens also start from a valid Left/Right-only layout.
    persistLayout(mode, frame);
  }, [frame, mode, persistLayout]);

  const closeExternalMonitor = useCallback(async () => {
    const documentPipWindow = pipWindowRef.current;
    pipWindowRef.current = null;
    if (documentPipWindow && !documentPipWindow.closed) {
      try { documentPipWindow.close(); } catch {}
    }
    setPipWindow(null);
    setStandardPipActive(false);
    setExternalStatus('');
    const video = standardPipVideoRef.current;
    standardPipVideoRef.current = null;
    if (video && document.pictureInPictureElement === video) {
      try { await document.exitPictureInPicture?.(); } catch {}
    }
  }, []);

  useEffect(() => {
    if (!active) {
      closeExternalMonitor();
      setExternalStatus('');
      setRestoreExternal(false);
      setPanelState('open');
    }
  }, [active, closeExternalMonitor]);

  useEffect(() => () => { closeExternalMonitor(); }, [closeExternalMonitor]);

  const onVideoNode = useCallback((key, node, tile) => {
    if (node) videoNodesRef.current.set(key, { node, tile });
    else videoNodesRef.current.delete(key);
  }, []);

  const openDocumentPictureInPicture = useCallback(async () => {
    const api = window.documentPictureInPicture;
    if (!api?.requestWindow) return false;
    const win = await api.requestWindow({ width: 480, height: 320 });
    copyStylesToPictureInPicture(win.document);
    const mount = win.document.createElement('div');
    mount.id = 'participant-monitor-pip-root';
    win.document.body.replaceChildren(mount);
    pipWindowRef.current = win;
    setPipWindow(win);
    setStandardPipActive(false);
    setExternalStatus('Participant grid is always on top in Picture-in-Picture.');
    const close = () => {
      if (pipWindowRef.current === win) pipWindowRef.current = null;
      setPipWindow((current) => current === win ? null : current);
      setStandardPipActive(false);
      setExternalStatus('');
    };
    win.addEventListener('pagehide', close, { once: true });
    return true;
  }, []);

  const openStandardPictureInPicture = useCallback(async () => {
    if (!document.pictureInPictureEnabled) return false;
    const candidate = [...videoNodesRef.current.values()].find(({ node, tile }) =>
      !tile.local &&
      node?.requestPictureInPicture &&
      node.readyState >= 1
    ) || [...videoNodesRef.current.values()].find(({ node }) =>
      node?.requestPictureInPicture && node.readyState >= 1
    );
    if (!candidate) return false;
    await candidate.node.requestPictureInPicture();
    standardPipVideoRef.current = candidate.node;
    setStandardPipActive(true);
    setExternalStatus(`Always-on-top video: ${candidate.tile.name}. This browser supports one participant at a time.`);
    candidate.node.addEventListener('leavepictureinpicture', () => {
      if (standardPipVideoRef.current === candidate.node) standardPipVideoRef.current = null;
      setStandardPipActive(false);
      setExternalStatus('');
    }, { once: true });
    return true;
  }, []);

  const openAlwaysOnTop = useCallback(async () => {
    setExternalStatus('Opening Picture-in-Picture…');
    try {
      if (await openDocumentPictureInPicture()) {
        // Do not leave two participant monitors covering the presentation.
        // The compact return chip remains available in the meeting.
        setRestoreExternal(false);
        setPanelState('minimized');
        return true;
      }
      if (await openStandardPictureInPicture()) {
        // Standard video PiP is tied to the exact <video> element that opened
        // it. Unmounting the in-meeting surface would make several browsers
        // immediately leave PiP, so keep the already-small source monitor
        // mounted. Document PiP above can safely replace it with the return
        // chip because that window owns its own React surface.
        setRestoreExternal(false);
        setPanelState('open');
        setExternalStatus('Always-on-top participant video is active. Keep this small source monitor open, or use Minimize to close PiP and leave a restore chip.');
        return true;
      }
      setExternalStatus('Cross-app always-on-top is not supported by this browser. The draggable in-meeting monitor remains available.');
      setPanelState('open');
      return false;
    } catch (error) {
      setExternalStatus(
        error?.name === 'NotAllowedError'
          ? 'Picture-in-Picture needs a direct click and browser permission. Try again from this button.'
          : 'Picture-in-Picture could not open. The in-meeting monitor remains available.'
      );
      setPanelState('open');
      return false;
    }
  }, [openDocumentPictureInPicture, openStandardPictureInPicture]);

  const setDockMode = (nextMode) => {
    const normalizedMode = normalizeParticipantMonitorMode(nextMode);
    setMode(normalizedMode);
    setPanelState('open');
    setRestoreExternal(false);
    persistLayout(normalizedMode, frame);
  };

  const dockExternalMonitor = useCallback((nextMode) => {
    if (!DOCK_MODES.includes(nextMode)) return;

    // Always save the edge as the in-meeting fallback. Chromium currently
    // permits PiP placement in some desktop shells but intentionally leaves
    // final placement to the browser/operating system in others.
    setMode(nextMode);
    persistLayout(nextMode, frame);
    const targetWindow = pipWindowRef.current;
    if (!targetWindow) {
      setExternalStatus(
        `${nextMode[0].toUpperCase()}${nextMode.slice(1)} dock is saved for the in-meeting monitor. The browser controls this video Picture-in-Picture window.`
      );
      return;
    }

    const geometry = getPictureInPictureDockGeometry(nextMode, targetWindow);
    let placementRequested = false;
    try {
      targetWindow.resizeTo?.(geometry.width, geometry.height);
      placementRequested = typeof targetWindow.resizeTo === 'function';
    } catch {}
    try {
      targetWindow.moveTo?.(geometry.x, geometry.y);
      placementRequested = placementRequested || typeof targetWindow.moveTo === 'function';
    } catch {}
    setExternalStatus(
      placementRequested
        ? `${nextMode[0].toUpperCase()}${nextMode.slice(1)} position requested. Your browser or operating system controls the final Picture-in-Picture position.`
        : `${nextMode[0].toUpperCase()}${nextMode.slice(1)} dock is saved. Return to the meeting to use it because this browser controls Picture-in-Picture placement.`
    );
  }, [frame, persistLayout]);

  const minimizeExternalMonitor = useCallback(async () => {
    setRestoreExternal(true);
    setPanelState('minimized');
    await closeExternalMonitor();
  }, [closeExternalMonitor]);

  const closeSecondaryMonitor = useCallback(async () => {
    setRestoreExternal(false);
    setPanelState('closed');
    await closeExternalMonitor();
  }, [closeExternalMonitor]);

  const returnExternalMonitorToMeeting = useCallback(async () => {
    setRestoreExternal(false);
    setPanelState('open');
    await closeExternalMonitor();
  }, [closeExternalMonitor]);

  const beginDrag = (event) => {
    if ((typeof event.button === 'number' && event.button !== 0) || event.target.closest?.('button')) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      mode,
    };
  };
  const drag = (event) => {
    const start = dragRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const nextMode = event.clientX < window.innerWidth / 2 ? 'left' : 'right';
    if (nextMode !== start.mode) {
      start.mode = nextMode;
      setMode(nextMode);
    }
  };
  const endDrag = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const nextMode = dragRef.current.mode;
    dragRef.current = null;
    persistLayout(nextMode, frame);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const minimizePanel = () => {
    if (standardPipActive) {
      void minimizeExternalMonitor();
      return;
    }
    setRestoreExternal(false);
    setPanelState('minimized');
  };
  const closePanel = () => {
    setRestoreExternal(false);
    closeExternalMonitor();
    setPanelState('closed');
  };
  const reopenPanel = () => {
    setRestoreExternal(false);
    setPanelState('open');
  };

  if (!active) return null;

  const externalActive = Boolean(pipWindow || standardPipActive);

  const dockStyle = mode === 'left'
    ? { left: 12, top: 62, bottom: 'calc(var(--meeting-controls-height, 5.75rem) + 12px)', width: 'min(286px, calc(100vw - 24px))' }
    : { right: 12, top: 62, bottom: 'calc(var(--meeting-controls-height, 5.75rem) + 12px)', width: 'min(286px, calc(100vw - 24px))' };

  return <>
    {panelState === 'open' ? <aside
      ref={rootRef}
      className="absolute z-[245] flex flex-col overflow-hidden rounded-2xl border border-cyan-300/35 bg-slate-950/95 text-white shadow-[0_20px_70px_rgba(6,182,212,0.28)] backdrop-blur-xl"
      style={dockStyle}
      data-screen-share-participant-monitor="true"
      data-monitor-state="open"
      data-monitor-mode={mode}
      aria-label="Screen-share participant monitor"
      onPointerUp={endDrag}
    >
      <header
        className="flex shrink-0 cursor-move touch-none select-none items-center gap-2 border-b border-white/10 bg-slate-900 px-2 py-1.5"
        onPointerDown={beginDrag}
        onPointerMove={drag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        aria-label="Drag participant monitor left or right"
        title="Drag across the screen to dock left or right"
      >
        <FaArrowsAlt className="shrink-0 text-cyan-300" aria-hidden="true" />
        <strong className="min-w-0 flex-1 truncate text-xs">Participants · {tiles.length}</strong>
        <button type="button" onClick={openAlwaysOnTop} className="rounded-md bg-cyan-500/20 p-1.5 text-cyan-100 hover:bg-cyan-500/35" title="Open genuine always-on-top Picture-in-Picture" aria-label="Open participant monitor Picture-in-Picture">
          <FaExternalLinkAlt size={11} />
        </button>
        {pipWindow && <button type="button" onClick={closeExternalMonitor} className="rounded-md p-1.5 text-slate-300 hover:bg-white/10" title="Close Picture-in-Picture" aria-label="Close participant monitor Picture-in-Picture"><FaTimes size={11} /></button>}
        <button type="button" onClick={minimizePanel} className="rounded-md p-1.5 text-slate-300 hover:bg-white/10" title="Minimize participant monitor" aria-label="Minimize participant monitor"><FaMinus size={11} /></button>
        <button type="button" onClick={closePanel} className="rounded-md p-1.5 text-slate-300 hover:bg-white/10" title="Close participant monitor" aria-label="Close participant monitor"><FaTimes size={11} /></button>
      </header>
      <nav className="flex shrink-0 items-center justify-center gap-1 border-b border-white/10 bg-slate-900/80 px-1 py-1" aria-label="Participant monitor dock position">
        {DOCK_MODES.map((position) => (
          <button key={position} type="button" onClick={() => setDockMode(position)} className={`rounded px-1.5 py-1 text-[9px] font-bold capitalize ${mode === position ? 'bg-cyan-500 text-slate-950' : 'text-slate-300 hover:bg-white/10'}`} aria-pressed={mode === position}>
            {position}
          </button>
        ))}
      </nav>
      <div className="min-h-0 flex-1">
        <MonitorSurface tiles={tiles} surfaceId="inapp" onVideoNode={onVideoNode} vertical={mode === 'left' || mode === 'right'} />
      </div>
      {externalStatus && <p className="shrink-0 border-t border-white/10 bg-slate-900 px-2 py-1.5 text-[9px] leading-3 text-cyan-100" role="status">{externalStatus}</p>}
    </aside> : panelState === 'minimized' && externalActive ? (
      <button
        type="button"
        onClick={returnExternalMonitorToMeeting}
        className="absolute right-3 top-[4.25rem] z-[245] inline-flex items-center gap-2 rounded-full border border-emerald-300/35 bg-slate-950/95 px-3 py-2 text-xs font-bold text-emerald-100 shadow-xl backdrop-blur-xl hover:bg-emerald-950"
        aria-label={`Return participant Picture-in-Picture to meeting · ${tiles.length} participants`}
        data-screen-share-participant-monitor-reopen="external-active"
        data-monitor-state="external"
      >
        <FaExternalLinkAlt aria-hidden="true" />
        <span>Participant window active</span>
        <span className="text-[9px] uppercase tracking-wider text-emerald-300">Return</span>
      </button>
    ) : panelState === 'minimized' && restoreExternal ? (
      <button
        type="button"
        onClick={openAlwaysOnTop}
        className="absolute right-3 top-[4.25rem] z-[245] inline-flex items-center gap-2 rounded-full border border-cyan-300/35 bg-slate-950/95 px-3 py-2 text-xs font-bold text-cyan-100 shadow-xl backdrop-blur-xl hover:bg-cyan-950"
        aria-label={`Restore minimized participant Picture-in-Picture · ${tiles.length} participants`}
        data-screen-share-participant-monitor-reopen="external-minimized"
        data-monitor-state="external-minimized"
      >
        <FaExternalLinkAlt aria-hidden="true" />
        <span>{tiles.length} participants</span>
        <span className="text-[9px] uppercase tracking-wider text-cyan-300">Restore window</span>
      </button>
    ) : panelState === 'minimized' ? (
      <button
        type="button"
        onClick={reopenPanel}
        className="absolute right-3 top-[4.25rem] z-[245] inline-flex items-center gap-2 rounded-full border border-cyan-300/35 bg-slate-950/95 px-3 py-2 text-xs font-bold text-cyan-100 shadow-xl backdrop-blur-xl hover:bg-cyan-950"
        aria-label={`Reopen minimized participant monitor · ${tiles.length} participants`}
        data-screen-share-participant-monitor-reopen="minimized"
        data-monitor-state="minimized"
      >
        <FaTh aria-hidden="true" />
        <span>{tiles.length} participants</span>
        <span className="text-[9px] uppercase tracking-wider text-cyan-300">Reopen</span>
      </button>
    ) : (
      <button
        type="button"
        onClick={reopenPanel}
        className="absolute right-3 top-[4.25rem] z-[245] inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/90 px-3 py-2 text-xs font-semibold text-white shadow-xl backdrop-blur-xl hover:bg-slate-800"
        aria-label="Open participant monitor"
        data-screen-share-participant-monitor-reopen="closed"
        data-monitor-state="closed"
      >
        <FaTh aria-hidden="true" />
        <span>Participants</span>
      </button>
    )}

    {pipWindow && pipWindow.document?.getElementById('participant-monitor-pip-root') && createPortal(
      <div
        data-secondary-participant-monitor="true"
        style={{ width: '100%', height: '100%', display: 'grid', gridTemplateRows: '38px 31px minmax(0,1fr) auto', background: '#020617' }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 7px', color: 'white', background: '#0f172a', fontSize: 12, fontWeight: 700 }}>
          <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Shepherds participants · {tiles.length} live
          </span>
          <button
            type="button"
            onClick={returnExternalMonitorToMeeting}
            style={PIP_CONTROL_STYLE}
            title="Return monitor to the meeting"
            aria-label="Return secondary participant monitor to meeting"
          >
            <FaCompressAlt aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={minimizeExternalMonitor}
            style={PIP_CONTROL_STYLE}
            title="Minimize participant window"
            aria-label="Minimize secondary participant monitor"
          >
            <FaMinus aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={closeSecondaryMonitor}
            style={PIP_CONTROL_STYLE}
            title="Close participant window"
            aria-label="Close secondary participant monitor"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </header>
        <nav
          aria-label="Secondary participant monitor dock position"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '3px 6px', background: '#111827', borderBottom: '1px solid rgba(255,255,255,.1)' }}
        >
          {DOCK_MODES.map((position) => (
            <button
              key={position}
              type="button"
              onClick={() => dockExternalMonitor(position)}
              style={{
                ...PIP_CONTROL_STYLE,
                minWidth: 48,
                height: 23,
                padding: '0 8px',
                color: mode === position ? '#082f49' : '#e2e8f0',
                background: mode === position ? '#67e8f9' : 'rgba(255,255,255,.06)',
                textTransform: 'capitalize',
              }}
              aria-pressed={mode === position}
              aria-label={`Dock secondary participant monitor ${position}`}
            >
              {position}
            </button>
          ))}
        </nav>
        <MonitorSurface tiles={tiles} surfaceId="pip" onVideoNode={onVideoNode} />
        {externalStatus && (
          <p style={{ margin: 0, padding: '5px 8px', borderTop: '1px solid rgba(255,255,255,.1)', background: '#0f172a', color: '#a5f3fc', fontSize: 9, lineHeight: 1.3 }} role="status">
            {externalStatus}
          </p>
        )}
      </div>,
      pipWindow.document.getElementById('participant-monitor-pip-root')
    )}
  </>;
};

export default ScreenShareParticipantMonitor;
